import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import type { RoastComment } from 'features/roast/utils';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

interface IGetRoastCommentsQueryFnParams {
  supabase: SupabaseClient<Database>;
  roastId: string;
}

const getRoastCommentsQueryFn = async ({
  supabase,
  roastId,
}: IGetRoastCommentsQueryFnParams): Promise<RoastComment[]> => {
  const { data, error } = await supabase
    .from('roast_comment')
    .select('id, roast_id, user_id, content, created_at')
    .eq('roast_id', roastId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Fetch user info for each comment
  const userIds = Array.from(new Set((data ?? []).map((c: { user_id: string }) => c.user_id)));

  let userMap: Record<string, { screen_name: string; avatar_url: string | null }> = {};

  if (userIds.length > 0) {
    const { data: users } = await supabase
      .from('user_profile_view')
      .select('id, screen_name, avatar_url')
      .in('id', userIds);

    if (users) {
      userMap = Object.fromEntries(
        (users as unknown as { id: string; screen_name: string; avatar_url: string | null }[]).map(
          (u) => [u.id, { screen_name: u.screen_name, avatar_url: u.avatar_url }]
        )
      );
    }
  }

  return (data ?? []).map((c: Record<string, unknown>) => ({
    id: c.id as string,
    roast_id: c.roast_id as string,
    user_id: c.user_id as string,
    content: c.content as string,
    created_at: c.created_at as string,
    screen_name: userMap[c.user_id as string]?.screen_name ?? 'Anonymous',
    avatar_url: userMap[c.user_id as string]?.avatar_url ?? undefined,
  }));
};

interface IGetRoastCommentsQueryParams {
  roastId?: string | null;
}

interface IGetRoastCommentsQueryResponse {
  data: RoastComment[] | null;
  isPending: boolean;
}

export const useGetRoastCommentsQuery = ({
  roastId,
}: IGetRoastCommentsQueryParams): IGetRoastCommentsQueryResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetRoastComments, roastId],
    queryFn: () =>
      getRoastCommentsQueryFn({
        supabase,
        roastId: roastId!,
      }),
    enabled: Boolean(roastId),
  });

  return {
    data: data ?? null,
    isPending,
  };
};
